import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Plus, Archive, ArchiveRestore, Edit3, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

export default function Collections() {
  const { role } = useAuth();
  const navigate = useNavigate();
  const canManage = ['admin','manager'].includes(role);
  const openDetail = (id) => navigate(`/collections/${id}`);
  const [list, setList] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try { setList(await apiFetch('/collections')); }
    catch (e) { toast.error(e.message); }
  };
  useEffect(() => { load(); }, []);

  const archive = async (id) => {
    try {
      await apiFetch(`/collections/${id}/archive`, { method: 'POST' });
      toast.success('Updated'); load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-8 lg:py-12 max-w-[1200px]" data-testid="collections-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
        <div>
          <div className="velvet-eyebrow mb-3">Catalog</div>
          <h1 className="velvet-page-title">Collections</h1>
          <p className="text-sm text-muted-foreground mt-3">Group signature arrangements by occasion, season, or line.</p>
        </div>
        {canManage && (
          <Button onClick={() => { setEditing(null); setDialogOpen(true); }} className="rounded-sm bg-foreground text-background hover:bg-foreground/90" data-testid="new-collection-btn">
            <Plus className="h-4 w-4 mr-1" /> New collection
          </Button>
        )}
      </div>

      <div className="velvet-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b-2 border-foreground/15 text-left">
              <th className="px-6 py-3 velvet-eyebrow">Name</th>
              <th className="px-6 py-3 velvet-eyebrow">Description</th>
              <th className="px-6 py-3 velvet-eyebrow text-right">Products</th>
              <th className="px-6 py-3 velvet-eyebrow">Status</th>
              {canManage && <th className="px-6 py-3 velvet-eyebrow text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {list.map(c => (
              <tr
                key={c.id}
                onClick={() => openDetail(c.id)}
                className="border-b border-border cursor-pointer hover:bg-[hsl(var(--muted))]/60 transition-colors"
                data-testid={`collection-row-${c.code}`}
              >
                <td className="px-6 py-4">
                  <button
                    onClick={(e) => { e.stopPropagation(); openDetail(c.id); }}
                    className="text-left group"
                    data-testid={`collection-name-${c.code}`}
                  >
                    <div className="font-serif text-lg group-hover:underline underline-offset-4 decoration-foreground/30 flex items-center gap-2">
                      {c.name}
                      <ChevronRight className="h-4 w-4 opacity-0 group-hover:opacity-60 transition-opacity" />
                    </div>
                    <div className="text-xs font-mono text-muted-foreground">{c.code}</div>
                  </button>
                </td>
                <td className="px-6 py-4 text-muted-foreground max-w-md truncate">{c.description || '—'}</td>
                <td className="px-6 py-4 text-right">
                  <button
                    onClick={(e) => { e.stopPropagation(); openDetail(c.id); }}
                    className="velvet-money hover:underline underline-offset-4"
                    data-testid={`collection-count-${c.code}`}
                  >
                    {c.product_count}
                  </button>
                </td>
                <td className="px-6 py-4">
                  <span className={`text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 border ${c.is_archived ? 'border-foreground bg-foreground text-background' : 'border-[hsl(var(--muted-green))]/50 text-[hsl(var(--muted-green-dark))]'}`}>
                    {c.is_archived ? 'Archived' : 'Active'}
                  </span>
                </td>
                {canManage && (
                  <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="inline-flex gap-2">
                      <Button size="sm" variant="outline" className="rounded-sm" onClick={() => { setEditing(c); setDialogOpen(true); }} data-testid={`edit-collection-${c.code}`}>
                        <Edit3 className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-sm" onClick={() => archive(c.id)} data-testid={`archive-collection-${c.code}`}>
                        {c.is_archived ? <ArchiveRestore className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />}
                      </Button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={canManage?5:4} className="p-8 text-center text-muted-foreground">No collections yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <CollectionDialog open={dialogOpen} editing={editing} onClose={() => setDialogOpen(false)} onSaved={() => { setDialogOpen(false); load(); }} />
    </div>
  );
}

function CollectionDialog({ open, editing, onClose, onSaved }) {
  const [form, setForm] = useState({ code:'', name:'', description:'' });
  useEffect(() => {
    if (editing) setForm({ code: editing.code, name: editing.name, description: editing.description || '' });
    else setForm({ code:'', name:'', description:'' });
  }, [editing, open]);

  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.name.trim()) return toast.error('Name required');
    setBusy(true);
    try {
      if (editing) {
        await apiFetch(`/collections/${editing.id}`, { method: 'PATCH', body: JSON.stringify({ name: form.name, description: form.description }) });
        toast.success('Collection updated');
      } else {
        await apiFetch('/collections', { method: 'POST', body: JSON.stringify(form) });
        toast.success('Collection created');
      }
      onSaved();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-md rounded-sm" data-testid="collection-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl">{editing ? 'Edit' : 'New'} collection</DialogTitle></DialogHeader>
        <div className="space-y-4 pt-2">
          {!editing && (
            <div>
              <Label>Code</Label>
              <Input value={form.code} onChange={e => setForm({...form, code: e.target.value})} className="rounded-sm font-mono" placeholder="e.g. signature" data-testid="coll-code-input" />
            </div>
          )}
          <div>
            <Label>Name</Label>
            <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="rounded-sm" data-testid="coll-name-input" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="rounded-sm" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
          <Button disabled={busy} onClick={submit} className="rounded-sm bg-foreground text-background hover:bg-foreground/90" data-testid="save-collection">
            {editing ? 'Save' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
